import { useParams, useSearchParams } from 'react-router';

import { featuredBrands } from '@/config/brands';
import { useVehicleFacets, useVehicleList } from '@/hooks/useVehicles';
import { filtersFromParams, formFromFilters } from '@/schemas/vehicleSearch';
import { type VehicleListFilters } from '@/services/vehicles/types';

const PAGE_SIZE = 24;

/** Everything /cars, /cars/:make and /cars/:make/:model need, derived from the URL. */
export function useCarsPage() {
  const { make: makeParam, model: modelParam } = useParams();
  const [searchParams] = useSearchParams();
  const facets = useVehicleFacets();

  const fromQuery = filtersFromParams(searchParams);
  const filters: VehicleListFilters = { ...fromQuery, pageSize: PAGE_SIZE };
  if (makeParam) filters.makeSlug = makeParam;
  if (modelParam) filters.modelSlug = modelParam;

  const list = useVehicleList(filters);

  const stockMake = facets.data?.makes.find((m) => m.slug === makeParam);
  const featured = featuredBrands.find((b) => b.slug === makeParam);
  const stockModel = stockMake?.models.find((m) => m.slug === modelParam);
  const makeName = stockMake?.name ?? featured?.name ?? list.data?.items[0]?.make;
  const modelName = stockModel?.name ?? list.data?.items[0]?.model;

  // A featured make always has a page (it is in the navigation); anything else
  // must exist in the listings. Unknown until the facets have loaded.
  const facetsReady = facets.isSuccess;
  const notFound =
    facetsReady &&
    ((makeParam !== undefined && !stockMake && !featured) ||
      (modelParam !== undefined && !stockModel));

  const hasQueryFilters = [...searchParams.keys()].some((k) => k !== 'page');

  return {
    makeParam,
    modelParam,
    makeName,
    modelName,
    filters,
    formDefaults: formFromFilters(filters),
    sort: fromQuery.sort ?? 'newest',
    page: fromQuery.page ?? 1,
    pageCount: list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1,
    list,
    notFound,
    hasQueryFilters,
  };
}
